import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { FREQ_LABEL, KIND_META, todayISO } from "@/lib/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const EMPTY = { kind: "evento", title: "", date: "", time: "", end_time: "", notes: "", freq: "none", ends: "never", count: "", until: "" };
const sel = "w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1a73e8]";

const toForm = (o, date) => o
  ? { ...EMPTY, kind: o.kind, title: o.title, date: o.series_start, time: o.time || "", end_time: o.end_time || "", notes: o.notes,
      freq: o.freq, ends: o.count ? "count" : o.until ? "until" : "never", count: o.count || "", until: o.until || "" }
  : { ...EMPTY, date: date || todayISO() };

const Field = ({ label, children }) => (
  <label className="block"><span className="block text-[11px] font-bold text-slate-500 mb-1">{label}</span>{children}</label>
);

export default function CalendarItemDialog({ open, onOpenChange, editing, defaultDate, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF(toForm(editing, defaultDate)); }, [open, editing, defaultDate]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.title.trim()) return toast.error("Informe o título");
    const body = {
      kind: f.kind, title: f.title, date: f.date, time: f.time || null, end_time: f.end_time || null, notes: f.notes, freq: f.freq,
      count: f.freq !== "none" && f.ends === "count" ? Number(f.count) || null : null,
      until: f.freq !== "none" && f.ends === "until" ? f.until || null : null,
    };
    setSaving(true);
    try {
      if (editing) await api.put(`/calendar/items/${editing.item_id}`, body);
      else await api.post("/calendar/items", body);
      toast.success(editing ? "Agendamento atualizado" : "Agendamento criado");
      onOpenChange(false);
      onSaved?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Falha ao salvar"); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="cal-item-dialog">
        <DialogHeader><DialogTitle>{editing ? "Editar agendamento" : "Novo agendamento"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-2" data-testid="cal-kind-toggle">
            {Object.entries(KIND_META).map(([k, m]) => (
              <button key={k} type="button" onClick={() => setF((p) => ({ ...p, kind: k }))} data-testid={`cal-kind-${k}`}
                className={`h-10 rounded-full text-sm font-bold border-2 transition-colors ${f.kind === k ? "text-white" : "bg-white text-slate-600 border-slate-200"}`}
                style={f.kind === k ? { background: m.color, borderColor: m.color } : undefined}>{m.label}</button>
            ))}
          </div>
          <Field label="Título"><Input value={f.title} onChange={set("title")} placeholder="Ex.: Pagar contabilidade" data-testid="cal-title" autoFocus /></Field>
          <Field label="Data"><Input type="date" value={f.date} onChange={set("date")} required data-testid="cal-date" /></Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Hora (opcional)"><Input type="time" value={f.time} onChange={set("time")} data-testid="cal-time" /></Field>
            <Field label="Até"><Input type="time" value={f.end_time} onChange={set("end_time")} disabled={!f.time} data-testid="cal-end-time" /></Field>
          </div>
          <Field label="Repetir">
            <select value={f.freq} onChange={set("freq")} className={sel} data-testid="cal-freq">
              {Object.entries(FREQ_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </Field>
          {f.freq !== "none" && (
            <div className="grid grid-cols-2 gap-2">
              <Field label="Termina">
                <select value={f.ends} onChange={set("ends")} className={sel} data-testid="cal-ends">
                  <option value="never">Nunca</option><option value="count">Após X vezes</option><option value="until">Em uma data</option>
                </select>
              </Field>
              {f.ends === "count" && <Field label="Nº de vezes"><Input type="number" min="1" max="1000" value={f.count} onChange={set("count")} required data-testid="cal-count" /></Field>}
              {f.ends === "until" && <Field label="Até o dia"><Input type="date" value={f.until} onChange={set("until")} required data-testid="cal-until" /></Field>}
            </div>
          )}
          <Field label="Descrição"><Textarea rows={3} value={f.notes} onChange={set("notes")} placeholder="Detalhes, pix, telefone…" data-testid="cal-notes" /></Field>
          <Button type="submit" disabled={saving} className="w-full h-11 rounded-full bg-[#1a73e8] hover:bg-[#1765cc] text-white font-bold" data-testid="cal-save">
            {saving ? "Salvando…" : "Salvar"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
